import { useCallback, useMemo, useRef } from 'react';
import useGridApi from 'common/providers/AgGridProvider/hooks/useGridApi';
import { APP_CONSTANTS } from 'constants/index';

interface GridState {
    pageNumber: number;
    pageSize: number;
}

interface StoreGridStateResult {
    restoreGridState: () => void;
    saveGridState: () => void;
    getGridState: () => GridState | null;
}

const useStoreGridState = (): StoreGridStateResult => {
    const [api] = useGridApi();
    const isRestoredRef = useRef(false);

    const getGridState = useCallback((): GridState | null => {
        const gridStateValue = localStorage.getItem(APP_CONSTANTS.QUEUE_STATE);

        if (!gridStateValue) {
            return null;
        }

        try {
            const gridState = JSON.parse(gridStateValue);
            if (!gridState) {
                return null;
            }

            return gridState;
        } catch (ex) {
            console.error('failed to parse the queue state from the localStorage', ex);
            return null;
        }
    }, []);

    const restoreGridState = useCallback((): void => {
        if (!api.current) {
            return;
        }

        isRestoredRef.current = true;

        try {
            const gridState = getGridState();
            
            if (!gridState) {
                return;
            }

            const {
                pageNumber = 0,
            } = gridState;

            api.current.paginationGoToPage(pageNumber);
        } catch (ex) {
            console.error('failed to parse the queue state from the localStorage', ex);
        }
    }, [api, getGridState]);

    const saveGridState = useCallback((): void => {
        if (!api.current?.paginationGetCurrentPage) {
            return;
        }

        if (!isRestoredRef.current) {
            return;
        }
        
        const gridState: GridState = {
            pageNumber: api.current.paginationGetCurrentPage(),
            pageSize: api.current.paginationGetPageSize(),
        }

        localStorage.setItem(APP_CONSTANTS.QUEUE_STATE, JSON.stringify(gridState));
    }, [api]);

    return useMemo((): StoreGridStateResult => ({
        saveGridState,
        restoreGridState,
        getGridState,
    }), [saveGridState, restoreGridState, getGridState]);
}

export default useStoreGridState;